import { useState, type FC, type FormEvent } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { Shield, Loader2, Sparkles, Copy, Check } from 'lucide-react';

const COMPANY_SIZES = [
  { value: '', label: 'Toutes tailles' },
  { value: 'startup', label: 'Startup (1-50)' },
  { value: 'pme', label: 'PME (50-250)' },
  { value: 'eti', label: 'ETI (250-5000)' },
  { value: 'grand_compte', label: 'Grand compte (5000+)' },
] as const;

const PERSONAS = [
  { value: '', label: 'Tous personas' },
  { value: 'cto', label: 'CTO' },
  { value: 'product_owner', label: 'Product Owner' },
  { value: 'ceo_startup', label: 'CEO Startup' },
  { value: 'qa_lead', label: 'QA Lead' },
  { value: 'vp_product', label: 'VP Product' },
] as const;

interface BattleCardResult {
  sector: string;
  rawContent?: string;
  keyArguments?: string[];
  objectionResponses?: { objection: string; response: string }[];
  roiEstimate?: string;
  demoSuggestions?: string[];
}

interface BattleCardGeneratorProps {
  onGenerated: () => void;
}

const CopyBtn: FC<{ text: string; label?: string }> = ({ text, label = 'Copier' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copié !' : label}
    </button>
  );
};

const BattleCardGenerator: FC<BattleCardGeneratorProps> = ({ onGenerated }) => {
  const [sector, setSector] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [mainCompetitor, setMainCompetitor] = useState('');
  const [persona, setPersona] = useState('');
  const [result, setResult] = useState<BattleCardResult | null>(null);

  const { mutate, isLoading } = useCustomMutation();

  const isValid = sector.trim().length >= 3;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isValid || isLoading) return;
    mutate(
      {
        url: '/api/v1/admin/growth/sales/battle-card',
        method: 'post',
        values: {
          sector: sector.trim(),
          companySize: companySize || undefined,
          mainCompetitor: mainCompetitor.trim() || undefined,
          persona: persona || undefined,
        },
      },
      {
        onSuccess: (response) => {
          const d = response?.data?.data ?? response?.data ?? {};
          setResult(d as BattleCardResult);
          onGenerated();
        },
      },
    );
  };

  const inputClasses =
    'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none transition-colors';

  const selectClasses =
    'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-sm text-slate-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 outline-none transition-colors';

  return (
    <div className="space-y-5">
      {/* Form */}
      <div className="fc-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Battle Card Generator</h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                Secteur <span className="text-red-500">*</span>
              </label>
              <input type="text" value={sector} onChange={(e) => setSector(e.target.value)} placeholder="Ex: SaaS B2B, FinTech, E-commerce..." className={inputClasses} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Taille entreprise</label>
              <select value={companySize} onChange={(e) => setCompanySize(e.target.value)} className={selectClasses}>
                {COMPANY_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Concurrent principal</label>
              <input type="text" value={mainCompetitor} onChange={(e) => setMainCompetitor(e.target.value)} placeholder="Ex: Hotjar, Typeform..." className={inputClasses} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Persona</label>
              <select value={persona} onChange={(e) => setPersona(e.target.value)} className={selectClasses}>
                {PERSONAS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <button type="submit" disabled={!isValid || isLoading} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {isLoading ? 'Génération...' : 'Générer Battle Card'}
            </button>
          </div>
        </form>
      </div>

      {/* Result */}
      {result && (
        <div className="fc-card p-5 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Battle Card — {result.sector}</h3>
            <CopyBtn text={result.rawContent ?? ''} label="Copier tout" />
          </div>

          {/* Fallback: raw Markdown when no structured fields */}
          {!result.keyArguments?.length && !result.objectionResponses?.length && !result.roiEstimate && !result.demoSuggestions?.length && result.rawContent && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-4">
              <pre className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-sans">{result.rawContent}</pre>
            </div>
          )}

          {/* Key Arguments */}
          {result.keyArguments && result.keyArguments.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Arguments clés</p>
                <CopyBtn text={result.keyArguments.join('\n')} />
              </div>
              <ul className="space-y-1.5">
                {result.keyArguments.map((arg, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                    {arg}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Objection Responses */}
          {result.objectionResponses && result.objectionResponses.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Objections & Réponses</p>
                <CopyBtn text={result.objectionResponses.map((o) => `Q: ${o.objection}\nR: ${o.response}`).join('\n\n')} />
              </div>
              <div className="space-y-2">
                {result.objectionResponses.map((item, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3">
                    <p className="text-xs font-semibold text-red-600 dark:text-red-400 mb-1">Objection : {item.objection}</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{item.response}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ROI */}
          {result.roiEstimate && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Estimation ROI</p>
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-3">
                <p className="text-sm text-emerald-800 dark:text-emerald-300">{result.roiEstimate}</p>
              </div>
            </div>
          )}

          {/* Demo Suggestions */}
          {result.demoSuggestions && result.demoSuggestions.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Suggestions démo</p>
              <ul className="space-y-1.5">
                {result.demoSuggestions.map((sug, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <span className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 dark:bg-amber-400" />
                    {sug}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BattleCardGenerator;
